import jsonpath from 'jsonpath'
import { WidgetConfig } from '@/types/dashboard'

export const MIN_PROMPT_LENGTH = 20

/* -------------------------------------
 * JSONPath helpers
 * ----------------------------------- */
/**
 * Normalize path to valid JSONPath
 * Allows using:
 *  - '$.a.b.c'
 *  - 'a.b.c'
 */
function normalizeJPath (path: string): string {
    if (path.startsWith('$')) return path
    return `$.${path}`
}

/**
 * Get value from object by JSON path
 */
export function getByJPath<T = string> (
    obj: object,
    path: string
): T | undefined {
    return jsonpath.value(obj, normalizeJPath(path))
}

/**
 * Set value in object by JSON path
 */
export function setByJPath (
    obj: unknown,
    path: string,
    value: unknown
): void {
    const normalized = normalizeJPath(path)
    jsonpath.value(obj, normalized, value)
}

/* -------------------------------------
 * Validation
 * ----------------------------------- */

/**
 * Validate a single prompts config
 * Checks if all tabs have valid values
 */
export function validatePromptsConfig (
    config: WidgetConfig
): boolean {
    const tabs = config.ParameterData?.tabs ?? []
    if (!tabs.length) return true

    const values = config.WidgetParameterValueJson ?? {}

    return tabs.every(tab => {
        const value = getByJPath(values, tab.jpath)

        return (
            typeof value === 'string' &&
            value.trim().length >= MIN_PROMPT_LENGTH
        )
    })
}

/* -------------------------------------
 * Composable
 * ----------------------------------- */

export default function usePromptsValidation () {
    return {
        MIN_PROMPT_LENGTH,
        getByJPath,
        setByJPath,
        validatePromptsConfig
    }
}
